import { useEffect, useMemo, useRef, useState } from 'react';
import { FilePicker } from './components/FilePicker';
import { Viewer } from './components/Viewer';
import { type LoadedDocument } from './lib/document';
import { buildRasterizedDoc, type LibraryItem } from './components/LibraryPicker';
import { useT } from './i18n';

export default function App() {
  const t = useT();
  const tRef = useRef(t);
  tRef.current = t;

  const [doc, setDoc] = useState<LoadedDocument | null>(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoError, setAutoError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fileQuery = params.get('file');
    if (!fileQuery) return;

    let cancelled = false;
    setAutoLoading(true);
    (async () => {
      try {
        const catRes = await fetch('/library/catalog.json', { cache: 'no-store' });
        if (!catRes.ok) throw new Error('catalog ' + catRes.status);
        const catalog = (await catRes.json()) as LibraryItem[];
        const item = findInCatalog(catalog, fileQuery);
        if (!item) {
          if (!cancelled) {
            setAutoError(`${tRef.current('picker.error.notInLibrary')}: ${fileQuery}`);
          }
          return;
        }
        if (item.pageCount && item.pageCount > 0) {
          if (cancelled) return;
          setDoc(buildRasterizedDoc(item));
          return;
        }
        // Fallback на pdf.js для файлов без растеризации
        const { loadDocument } = await import('./lib/document');
        const fileRes = await fetch(`/library/${item.id}.pdf`, { cache: 'no-store' });
        if (!fileRes.ok) throw new Error('file ' + fileRes.status);
        const blob = await fileRes.blob();
        const fileName = item.title.toLowerCase().endsWith('.pdf') ? item.title : item.title + '.pdf';
        const file = new File([blob], fileName, { type: 'application/pdf' });
        const loaded = await loadDocument(file);
        if (cancelled) return;
        setDoc({ ...loaded, transient: true });
      } catch (e) {
        if (!cancelled) setAutoError((e as Error).message || 'load failed');
      } finally {
        if (!cancelled) setAutoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // ВАЖНО: пустые deps. t достаётся через ref, иначе useEffect крутит бесконечный цикл fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sp = new URLSearchParams(window.location.search);
  const forceSend = sp.get('send') === 'true';
  const fileParam = sp.get('file') || '';
  const studid = sp.get('studid') || '';
  const lessonid = sp.get('lessonid') || '';
  const sig = sp.get('sig') || '';
  // useMemo обязателен: иначе новый объект каждый рендер триггерит useEffect в Viewer и
  // auto-fullscreen handler регистрируется заново — fullscreen откроется на каждом тапе.
  // fileId — тот же ID по которому CRM подписывала (hash или basename); backend проверяет sig по нему.
  const homework = useMemo(
    () => (studid ? { fileId: fileParam, studid, lessonid, sig } : undefined),
    [fileParam, studid, lessonid, sig]
  );

  if (doc) return <Viewer doc={doc} onClose={() => setDoc(null)} forceSend={forceSend} homework={homework} />;
  return (
    <FilePicker
      onLoaded={setDoc}
      initialError={autoError}
      externalLoading={autoLoading}
    />
  );
}

function findInCatalog(catalog: LibraryItem[], query: string): LibraryItem | undefined {
  const lower = query.toLowerCase();
  // 1. полное имя (с расширением) — приоритет, т.к. однозначно
  let item = catalog.find((it) => (it.title || '').toLowerCase() === lower);
  if (item) return item;
  // 2. basename без расширения
  item = catalog.find((it) => stripExt(it.title).toLowerCase() === lower);
  if (item) return item;
  // 3. по id (хеш)
  return catalog.find((it) => (it.id || '').toLowerCase() === lower);
}

function stripExt(name: string): string {
  return name.replace(/\.[^.]+$/, '');
}
